/**
 * Real faber worker geometry: rebind the vendored faber.glb onto the
 * 16-bone placeholder rig so workers render the actual faber model through
 * the shared GPU-skinned atlas (render/anim.ts + render/skinned.ts).
 *
 * The GLB is a 45-mesh UE export (25-joint mannequin skeleton, centimetre
 * units under a 0.01 armature scale). The atlas bakes bone matrices for the
 * placeholder bind pose (BONE_OFFSETS), so each GLB vertex is re-expressed
 * in the placeholder bind space:
 *
 *   v_placeholder = P_pb * inverse(boneWorld_j) * meshWorld * v
 *
 * where j is the vertex's dominant skin joint, boneWorld_j its bind-pose
 * world matrix, and P_pb the placeholder bone's bind-pose world matrix.
 * Binding is rigid (one placeholder bone per vertex, like the procedural
 * humanoid) — reads fine at RTS camera distance and keeps the vertex
 * shader to its tight loop.
 *
 * Tool meshes (hammer, frame saw, adze, plumb bob, pouch tools) are
 * EXCLUDED from the merge: WorkerField keeps its instanced prop system so
 * tools only appear during the matching work activity (readability).
 *
 * Vertex colours come from each mesh's material base colour; the two
 * textured materials (skin, hair) fall back to averages sampled from the
 * real textures (2026-09-22). The worker material enables vertexColours;
 * the per-instance team tint multiplies on top.
 */
import * as THREE from "three";
import {
  BONE_COUNT,
  BONE_NAMES,
  BONE_OFFSETS,
  BONE_PARENTS,
  GLTF_BONE_REMAP,
  boneIndex,
} from "./anim";

/** GLB mesh-name fragments that stay as instanced props, not body. */
const TOOL_MESH_RE = /adze|frame_saw|hammer|plumb|pouch_tool/i;

/**
 * Joint -> placeholder bone for the geometry rebind. Extends
 * GLTF_BONE_REMAP (animation) with the bind-only joints the skin actually
 * weights to: spine_01/02 (no spine_03 joint in the skin), neck_01,
 * clavicles, and the prop sockets.
 */
const REBIND_EXTRA: Record<string, string> = {
  root: "hips",
  spine_01: "spine",
  spine_02: "chest",
  neck_01: "head",
  clavicle_l: "chest",
  clavicle_r: "chest",
  shield_socket_l: "handL",
  tool_socket_l: "handL",
  weapon_socket_r: "handR",
  tool_socket_r: "handR",
  carry_socket: "hips",
};

const JOINT_TO_BONE: Record<string, number> = {};
for (const [joint, bone] of Object.entries({
  ...GLTF_BONE_REMAP,
  ...REBIND_EXTRA,
})) {
  JOINT_TO_BONE[joint] = boneIndex(bone);
}

/**
 * Fallback vertex colours for the textured materials (linear-space
 * averages sampled from the real PNGs, 2026-09-22). Every other material
 * carries its baseColorFactor on material.color via GLTFLoader.
 */
const TEXTURED_FALLBACK: Record<string, [number, number, number]> = {
  M_Skin: [0x7f / 255, 0x3f / 255, 0x23 / 255],
  M_Hair: [0x0d / 255, 0x06 / 255, 0x03 / 255],
};

/** Placeholder bind-pose world matrices, index = placeholder bone. */
function placeholderWorlds(): THREE.Matrix4[] {
  const bones = BONE_NAMES.map((name) => {
    const o = new THREE.Object3D();
    o.name = name;
    return o;
  });
  for (let i = 0; i < BONE_COUNT; i++) {
    const off = BONE_OFFSETS[i];
    bones[i].position.set(off[0], off[1], off[2]);
    const p = BONE_PARENTS[i];
    if (p >= 0) bones[p].add(bones[i]);
  }
  bones[0].updateMatrixWorld(true);
  return bones.map((b) => b.matrixWorld.clone());
}

const tmpV = new THREE.Vector3();
const tmpN = new THREE.Vector3();

/**
 * Rebind a loaded faber GLB group onto the placeholder rig. Returns the
 * merged, atlas-ready geometry, or null when nothing usable was found
 * (caller keeps the procedural humanoid).
 */
export function buildFaberWorkerGeometry(
  group: THREE.Group,
): THREE.BufferGeometry | null {
  group.updateMatrixWorld(true);
  const pWorlds = placeholderWorlds();

  const pos: number[] = [];
  const norm: number[] = [];
  const col: number[] = [];
  const skinI: number[] = [];
  const skinW: number[] = [];

  let meshes = 0;
  group.traverse((obj) => {
    const mesh = obj as THREE.SkinnedMesh;
    if (!mesh.isSkinnedMesh || !mesh.skeleton) return;
    const name = (mesh.name || "").toLowerCase();
    if (TOOL_MESH_RE.test(name)) return;

    const skeleton = mesh.skeleton;
    const meshWorld = mesh.matrixWorld;
    // Per-joint rebind matrix: P_pb * inverse(boneWorld) * meshWorld.
    const rebind: (THREE.Matrix4 | null)[] = skeleton.bones.map((bone) => {
      const pb = JOINT_TO_BONE[bone.name.toLowerCase()] ?? -1;
      if (pb < 0) return null;
      return new THREE.Matrix4()
        .copy(pWorlds[pb])
        .multiply(new THREE.Matrix4().copy(bone.matrixWorld).invert())
        .multiply(meshWorld);
    });

    // Material colour for the whole mesh (one material per GLB mesh).
    const mat = mesh.material as THREE.Material | THREE.Material[];
    const first = Array.isArray(mat) ? mat[0] : mat;
    const fallback =
      first && TEXTURED_FALLBACK[first.name]
        ? TEXTURED_FALLBACK[first.name]
        : null;
    const mc = new THREE.Color(1, 1, 1);
    if (fallback) mc.setRGB(fallback[0], fallback[1], fallback[2], THREE.LinearSRGBColorSpace);
    else if (first && "color" in first) mc.copy((first as THREE.MeshStandardMaterial).color);

    const geo = (mesh.geometry as THREE.BufferGeometry).toNonIndexed();
    const p = geo.getAttribute("position") as THREE.BufferAttribute;
    const n = geo.getAttribute("normal") as THREE.BufferAttribute;
    const si = geo.getAttribute("skinIndex") as THREE.BufferAttribute;
    const sw = geo.getAttribute("skinWeight") as THREE.BufferAttribute;
    if (!p || !n || !si || !sw) {
      geo.dispose();
      return;
    }
    for (let v = 0; v < p.count; v++) {
      // Dominant mapped joint (rigid binding, like the procedural mesh).
      let best = -1;
      let bestW = 0;
      for (let k = 0; k < 4; k++) {
        const j = si.array[v * 4 + k] | 0;
        const w = sw.array[v * 4 + k];
        if (w > bestW && j < rebind.length && rebind[j]) {
          bestW = w;
          best = j;
        }
      }
      if (best < 0) continue;
      const pb = JOINT_TO_BONE[skeleton.bones[best].name.toLowerCase()];
      const m = rebind[best]!;
      tmpV.set(p.getX(v), p.getY(v), p.getZ(v)).applyMatrix4(m);
      pos.push(tmpV.x, tmpV.y, tmpV.z);
      tmpN.set(n.getX(v), n.getY(v), n.getZ(v)).transformDirection(m);
      norm.push(tmpN.x, tmpN.y, tmpN.z);
      col.push(mc.r, mc.g, mc.b);
      skinI.push(pb, 0, 0, 0);
      skinW.push(1, 0, 0, 0);
    }
    geo.dispose();
    meshes++;
  });

  if (meshes === 0 || pos.length === 0) return null;
  const out = new THREE.BufferGeometry();
  out.setAttribute("position", new THREE.Float32BufferAttribute(pos, 3));
  out.setAttribute("normal", new THREE.Float32BufferAttribute(norm, 3));
  out.setAttribute("color", new THREE.Float32BufferAttribute(col, 3));
  out.setAttribute("skinIndex", new THREE.Float32BufferAttribute(skinI, 4));
  out.setAttribute("skinWeight", new THREE.Float32BufferAttribute(skinW, 4));
  return out;
}
